/**
 * Einstempel-Modal — User wählt einen Standort, wir legen sofort eine
 * Open-End-Schicht mit Start=jetzt und endTime=startTime an. Der User
 * stempelt später aus (oder bekommt um 21:00 eine Push-Erinnerung).
 *
 * Wenn dem User Standorte zugeordnet sind (allowedLocationIds), zeigen wir
 * nur diese. Sonst alle aktiven Standorte.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useCreateShift } from '../data/shifts';
import { useLocations } from '../data/locations';
import { useAuth, useCanManage } from '../store/auth';
import { hapticError, hapticSuccess } from '../lib/haptics';
import { showClockedInNotification } from '../lib/clockNotification';

interface Props {
  onClose: () => void;
  onClockedIn: () => void;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function nowHHMM(): string {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function ClockInModal({ onClose, onClockedIn }: Props) {
  const me = useAuth((s) => s.user);
  const create = useCreateShift();
  const allLocations = useLocations() ?? [];

  const isChef = useCanManage();
  const availableLocations = (() => {
    const onlyActive = allLocations.filter((l) => l.isActive);
    // v1.3.0: Chef stempelt an jedem Standort seiner Firma ein.
    if (!me || isChef || me.allowedLocationIds.length === 0) return onlyActive;
    return onlyActive.filter((l) => me.allowedLocationIds.includes(l.id));
  })();

  const [locationId, setLocationId] = useState<string>('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!locationId && availableLocations[0]) {
      setLocationId(availableLocations[0].id);
    }
  }, [availableLocations, locationId]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!locationId) {
      setError('Bitte einen Standort wählen.');
      hapticError();
      return;
    }
    const now = nowHHMM();
    try {
      const saved = await create.mutateAsync({
        locationId,
        shiftDate: todayIso(),
        startTime: now,
        endTime: now,        // Platzhalter — wird beim Ausstempeln gesetzt
        status: 'planned',   // wird beim Ausstempeln auf "done" gesetzt
        isOpenEnd: true,
        note: note.trim() ? note.trim() : null,
      });
      // Persistente System-Notification anzeigen
      const loc = availableLocations.find((l) => l.id === locationId);
      void showClockedInNotification(
        { id: saved.id, startTime: now },
        loc?.name ?? 'Standort',
      );
      hapticSuccess();
      onClockedIn();
    } catch (err) {
      hapticError();
      setError(err instanceof Error ? err.message : 'Einstempeln fehlgeschlagen');
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-slate-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-1">Einstempeln</h3>
        <p className="text-xs text-slate-400 mb-4">
          Aktuelle Uhrzeit: <span className="font-medium text-slate-200">{nowHHMM()}</span>
        </p>

        {availableLocations.length === 0 ? (
          <p className="text-sm text-amber-300">
            Keine aktiven Standorte verfügbar. Bitte einen Admin/Manager bitten,
            mindestens einen Standort freizuschalten.
          </p>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">Standort</span>
              <select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                className="input"
                required
              >
                {availableLocations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">
                Notiz (optional)
              </span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="input resize-y"
                placeholder="z. B. Vertretung"
              />
            </label>

            {error && <p className="text-sm text-red-400">{error}</p>}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 text-sm rounded bg-slate-700 hover:bg-slate-600"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={create.isPending}
                className="px-4 py-2 text-sm rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 font-medium"
              >
                {create.isPending ? 'Stempele ein…' : `Einstempeln um ${nowHHMM()}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
